<!-- src/components/BeneficiaryCommitmentModal.vue -->
<template>
  <div ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">
        <form @submit.prevent="submit">
          <div class="modal-header">
            <h5 class="modal-title">Set Beneficiary Commitment</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              :disabled="saving"
            ></button>
          </div>

          <div class="modal-body">
            <div v-if="error" class="alert alert-danger py-2">
              {{ error }}
            </div>

            <div class="mb-3">
              <div class="text-muted small">Beneficiary</div>
              <div class="fw-semibold">{{ beneficiary?.name || "—" }}</div>
              <div class="text-muted small">
                Guardian: {{ beneficiary?.guardian_name || "—" }}
              </div>
            </div>

            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="form-label">Amount</label>
                <input
                  v-model="form.amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  class="form-control"
                  :disabled="saving"
                />
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label">Cycle start period</label>
                <input
                  ref="periodInput"
                  class="form-control"
                  placeholder="Select period"
                  :disabled="saving"
                />
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";
import flatpickr from "flatpickr";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/plugins/monthSelect/style.css";

export default {
  name: "BeneficiaryCommitmentModal",
  emits: ["saved"],

  data() {
    return {
      modal: null,
      saving: false,
      error: "",
      beneficiary: null,
      periodPicker: null,
      form: {
        amount: "",
        cycle_start_period: "",
      },
    };
  },

  methods: {
    currentMonthKey() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${yyyy}-${mm}`;
    },

    initDatePicker() {
      if (!this.$refs.periodInput) return;

      if (this.periodPicker) {
        this.periodPicker.destroy();
        this.periodPicker = null;
      }

      this.periodPicker = flatpickr(this.$refs.periodInput, {
        plugins: [
          new monthSelectPlugin({
            shorthand: true,
            dateFormat: "Y-m",
            altFormat: "F Y",
          }),
        ],
        dateFormat: "Y-m",
        defaultDate: this.form.cycle_start_period || null,
        onChange: (_, dateStr) => {
          this.form.cycle_start_period = dateStr;
        },
      });
    },

    resetForm() {
      this.saving = false;
      this.error = "";
      this.beneficiary = null;

      if (this.periodPicker) {
        this.periodPicker.destroy();
        this.periodPicker = null;
      }

      this.form = {
        amount: "",
        cycle_start_period: "",
      };
    },

    open(beneficiary, period = null) {
      this.beneficiary = beneficiary || null;
      this.error = "";
      this.saving = false;
      this.form.amount = "";
      this.form.cycle_start_period = period || this.currentMonthKey();

      this.modal?.show();

      this.$nextTick(() => {
        this.initDatePicker();
      });
    },

    async submit() {
      this.error = "";

      try {
        if (!this.beneficiary?.id) {
          throw new Error("Beneficiary not selected.");
        }

        if (!this.form.amount || Number(this.form.amount) <= 0) {
          throw new Error("Enter a valid amount.");
        }

        if (!this.form.cycle_start_period) {
          throw new Error("Select cycle start period.");
        }

        this.saving = true;

        await http.post("/commitments", {
          beneficiary_id: this.beneficiary.id,
          amount: this.form.amount,
          cycle_start_period: this.form.cycle_start_period,
        });

        this.modal?.hide();
        this.$emit("saved");
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          Object.values(e?.response?.data?.errors || {})?.flat()?.[0] ||
          e?.message ||
          "Failed to save commitment.";
      } finally {
        this.saving = false;
      }
    },
  },

  mounted() {
    const el = this.$refs.modalEl;
    if (!el) return;

    this.modal = Modal.getOrCreateInstance(el);
    el.addEventListener("hidden.bs.modal", this.resetForm);
  },

  beforeUnmount() {
    const el = this.$refs.modalEl;
    if (el) el.removeEventListener("hidden.bs.modal", this.resetForm);

    if (this.periodPicker) {
      this.periodPicker.destroy();
      this.periodPicker = null;
    }
  },
};
</script>